import dayjs from "dayjs"
import DOMPurify from "dompurify"
import Fuse from "fuse.js"
import camelCase from "lodash/camelCase"
import mapKeys from "lodash/mapKeys"
import Geocode from "react-geocode"
import { createClient } from "urql"

import { browserIanaTimeZone } from "./timeZoneUtils"

export const OPACITY_75 = "BF"
export const OPACITY_25 = "40"
export const OPACITY_50 = "80"
export const OPACITY_40 = "66"

export function timeAgo(timestamp) {
  const time = new Date(timestamp).getTime()
  const now = new Date().getTime()
  const difference = Math.abs(now - time)

  const seconds = Math.floor(difference / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  const weeks = Math.floor(days / 7)
  const months = Math.floor(weeks / 4.345) // Average weeks in a month
  const years = Math.floor(months / 12)

  if (minutes < 60) {
    return minutes + " min ago"
  } else if (hours < 24) {
    return hours + " hour" + (hours !== 1 ? "s" : "") + " ago"
  } else if (days < 7) {
    return days + " day" + (days !== 1 ? "s" : "") + " ago"
  } else if (weeks < 4.345) {
    return weeks + " week" + (weeks !== 1 ? "s" : "") + " ago"
  } else if (months < 12) {
    return months + " month" + (months !== 1 ? "s" : "") + " ago"
  } else {
    return years + " year" + (years !== 1 ? "s" : "") + " ago"
  }
}

export function distanceInMiles(lat1, lon1, lat2, lon2) {
  const radiusOfEarthInMiles = 3958.8
  // Convert degrees to radians
  const rlat1 = lat1 * (Math.PI / 180)
  const rlat2 = lat2 * (Math.PI / 180)
  const difflat = rlat2 - rlat1 // Difference in latitude in radians
  const difflon = (lon2 - lon1) * (Math.PI / 180) // Difference in longitude in radians

  // Haversine formula
  return (
    2 *
    radiusOfEarthInMiles *
    Math.asin(
      Math.sqrt(
        Math.sin(difflat / 2) * Math.sin(difflat / 2) +
          Math.cos(rlat1) * Math.cos(rlat2) * Math.sin(difflon / 2) * Math.sin(difflon / 2)
      )
    )
  )
}

export const updateLocationSearchText = (latLng) => {
  Geocode.fromLatLng(latLng[0], latLng[1], process.env.GOOGLE_MAPS_API_KEY)
    .then((response) => {
      let results = response.results

      let result = results[0]

      let cityStateCountry = getCityFromGeoResult(result)

      const event = new CustomEvent("newLocationChosen", { detail: stripUSAFromString(cityStateCountry) })
      document.dispatchEvent(event)
    })
    .catch((error) => {
      console.error(error) // eslint-disable-line
    })
}

export function getCityFromGeoResult(result) {
  const { address_components } = result
  let city = ""
  let state = ""
  let country = ""

  const componentTypes = {
    CITY: ["locality", "postal_town", "administrative_area_level_2"],
    STATE: ["administrative_area_level_1"],
    COUNTRY: ["country"]
  }

  address_components.forEach((component) => {
    if (!city && componentTypes.CITY.some((type) => component.types.includes(type))) {
      city = component.long_name
    }
    if (!state && componentTypes.STATE.some((type) => component.types.includes(type))) {
      state = component.short_name || component.long_name
    }
    if (!country && componentTypes.COUNTRY.some((type) => component.types.includes(type))) {
      country = component.short_name === "US" ? "USA" : component.long_name
    }
  })

  return formatLocation(city, state, country)
}

function formatLocation(city, state, country) {
  if (city && state && country) {
    return `${city}, ${state}, ${country}`
  } else if (state && country) {
    return `${state}, ${country}`
  } else if (country) {
    return country
  }
  return ""
}

export function splitString({ string, numberWords }) {
  if (string.length > 0) {
    const stringArray = string.split(",")
    if (stringArray.length > 0) {
      if (stringArray[1] === "United States of America") {
        return stringArray[0]
      } else if (stringArray.slice(0, numberWords).join().length > 18) {
        return stringArray.slice(0, numberWords).join().substring(0, 18)
      } else {
        return stringArray.slice(0, numberWords).join()
      }
    }
  }
  return ""
}

export function stripUSAFromString(string) {
  return string.endsWith(", USA") ? string.replace(", USA", "") : string
}

export function formatPhone(phone) {
  const cleaned = ("" + phone).replace(/\D/g, "")
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/)
  if (match) {
    const intlCode = match[1] ? "+1 " : ""
    return [intlCode, "(", match[2], ") ", match[3], "-", match[4]].join("")
  }
  return phone
}

export function formatClientPhonePlusOne(phone) {
  if (!phone) return null
  let clientPhone = formatPhone(phone)
  if (clientPhone) {
    clientPhone = clientPhone.startsWith("+1") || clientPhone.startsWith("1") ? clientPhone : "+1 " + clientPhone
  }
  return clientPhone
}

/**
 * Normalizes a phone for create/invite client flows. US numbers get +1; any other
 * E.164 international prefix (+ not followed by 1) is left for Phonelib on the server.
 */
export function normalizePhoneForClientMutation(rawPhone) {
  const trimmed = (rawPhone || "").trim()
  if (!trimmed) return ""
  const collapsed = trimmed.replace(/\s/g, "")
  if (collapsed.startsWith("+") && !collapsed.startsWith("+1")) {
    return collapsed
  }
  return formatClientPhonePlusOne(trimmed) || ""
}

export function clientName(client) {
  let clientName = client.firstName
  if (client.lastName) {
    clientName += ` ${client.lastName}`
  }
  if (clientName?.length === 0) {
    clientName = client.email || client.phone
  }
  return clientName
}

export function isClientValid(clientInput, clientPhone, practicePhone, practiceEmail) {
  const { firstName, lastName, phone, email } = clientInput
  const emailTrimmed = email?.trim() || ""
  const phoneTrimmed = phone?.trim() || ""
  const hasValidEmail = emailTrimmed !== "" && Boolean(validEmail(emailTrimmed))
  const hasPhone = phoneTrimmed !== ""

  return (
    firstName?.trim() !== "" &&
    lastName?.trim() !== "" &&
    (hasValidEmail || hasPhone) &&
    (clientPhone !== practicePhone || !practicePhone) &&
    emailTrimmed !== practiceEmail
  )
}

export function formatPrice(price, currency = "USD", { alwaysShowDecimals = false } = {}) {
  const amount = Math.abs(price) / 100
  // Use toLocaleString for proper currency formatting (e.g., "CA$100" for CAD)
  // Only show decimal places if there are cents (e.g., "$8.55" but "$80" not "$80.00")
  // When alwaysShowDecimals is true (e.g. for CurrencyInput), always show 2 decimal places
  // so the user can type a decimal point without it being stripped on re-render
  const showDecimals = alwaysShowDecimals || amount % 1 !== 0
  const formatted = amount.toLocaleString("en-US", {
    style: "currency",
    currency: currency,
    minimumFractionDigits: showDecimals ? 2 : 0,
    maximumFractionDigits: 2
  })
  return formatted
}

export function unformatPrice(string) {
  if (!string) return 0
  const cleaned = string.replace(/[^0-9-.]/g, "")
  if (!cleaned) return 0
  const parsed = parseFloat(cleaned)
  if (isNaN(parsed)) return 0

  // Input always comes from CurrencyInput which displays dollar amounts
  // (e.g., "$1,900" -> "1900", "$1,900.50" -> "1900.50")
  // Always treat as dollars and convert to cents
  return Math.round(parsed * 100)
}

export function stringPriceToInteger(price) {
  return Math.round(parseFloat(price.replace(/[^0-9-.]/g, "")) * 100)
}

export function formatDate(date) {
  return dayjs(date).format("MMMM Do YYYY [at] h:mm a")
}

export function formatShortDate(date) {
  return dayjs(date).format("MM/DD/YYYY")
}

export const formatPackageSessionTimestamp = (date) => (date ? dayjs(date).format("MM/DD/YYYY h:mm:ss A") : "")

export function formatShortDateTime(date) {
  return dayjs(date).format("MM/DD/YYYY [at] h:mm a")
}

export function capitalize(string) {
  return string.charAt(0).toUpperCase() + string.slice(1)
}

export function appointmentStatusString(appointment) {
  if (appointment.newClient) {
    return "New client"
  } else if (appointment.state === "approved" && new Date(appointment.startsAt) < Date.now()) {
    return "Appointment completed"
  } else if (appointment.state === "cancelled") {
    return "Appointment cancelled"
  }
}

export function findBestMatchService(hit, query, isVirtual) {
  if (!hit.services) return null

  const servicesWithVariations = [].concat.apply(
    [],
    hit.services.map((service) => {
      if (service.variations) {
        return service.variations.map((variation) => ({ ...service, ...variation }))
      } else {
        return [service]
      }
    })
  )

  const fuse = new Fuse(servicesWithVariations.map((service) => service.name))
  const matches = fuse.search(query).map((match) => servicesWithVariations[match.refIndex])
  const services = query ? matches : servicesWithVariations

  const freeService = servicesWithVariations.find((service) => service.amount_cents === 0)

  const cheapestMatchingServiceAmount = Math.min(
    ...services.filter((service) => service.amount_cents > 0).map((service) => service.amount_cents)
  )
  const cheapestMatchingService = services.filter(
    (service) =>
      (isVirtual ? service.is_virtual === isVirtual : true) && service.amount_cents === cheapestMatchingServiceAmount
  )[0]

  const cheapestServiceAmount = Math.min(
    ...servicesWithVariations.filter((service) => service.amount_cents > 0).map((service) => service.amount_cents)
  )
  const cheapestService = servicesWithVariations.filter((service) => service.amount_cents === cheapestServiceAmount)[0]

  return cheapestMatchingService || cheapestService || freeService
}

export const truncate = (text, maxLength) => {
  if (text.length > maxLength) {
    let truncatedText = text.substring(0, maxLength - 3).trim()
    truncatedText = truncatedText.replace(/[.\s]+$/, "")

    return truncatedText + "..."
  }
  return text
}

export function findPhotoUrl(practitioner, service) {
  if (service.photoUrl.includes("defaults") && service.parent && !service.parent.photoUrl.includes("defualts")) {
    service.photoUrl = service.parent.photoUrl
    service.photos = service.parent.photos
  }
  let photoUrl = service.photos.large ? service.photos.large.jpeg : null
  if (!photoUrl) {
    photoUrl = service.photos.medium ? service.photos.medium.jpeg : null
  }
  if (!photoUrl) {
    photoUrl = practitioner?.filestack_photo?.large ? practitioner?.filestack_photo?.large?.jpeg : null
  }
  if (!photoUrl) {
    photoUrl = practitioner?.filestack_photo?.medium ? practitioner?.filestack_photo?.medium?.jpeg : null
  }
  return photoUrl
}

export function findAvailability(practice) {
  return practice.bookingAvailability?.availableSlots.filter((slot) => slot.datetimes.length > 0)
}

export function selectStyling() {
  const options = {
    menuPortal: (provided) => ({ ...provided, zIndex: 99999 }),
    menu: (provided) => ({ ...provided, zIndex: 99999 }),

    option: (provided, state) => ({
      ...provided,
      borderBottom: "transparent",
      padding: 16,
      backgroundColor: state.isSelected || state.isFocused ? "rgb(var(--color-ultra-light-gray))" : "#fff",
      color: "rgb(var(--color-black))"
    }),
    valueContainer: (provided) => ({ ...provided, padding: "0.375rem 0.75rem" }),
    dropdownIndicator: (provided) => ({ ...provided, color: "rgb(var(--color-dark-gray))" }),
    placeholder: (provided) => ({ ...provided, color: "rgb(var(--color-dark-gray))" }),
    input: (provided) => ({
      ...provided,
      color: "rgb(var(--color-dark-gray))",
      fontSize: "16px",
      paddingTop: 0,
      paddingBottom: 0,
      margin: 0
    }),
    indicatorSeparator: () => ({ display: "none" }),
    control: (provided, state) => ({
      ...provided,
      borderRadius: "0.5rem",
      boxShadow: state.isSelected || state.isFocused ? "0 0 0 1px rgb(var(--color-blue))" : "none",
      borderColor: "rgb(var(--color-gray))",
      "&:hover": {
        borderColor: "rgb(var(--color-black))"
      },
      "&:focus": {
        boxShadow: "0 0 0 3px rgb(var(--color-blue))",
        borderColor: "rgb(var(--color-gray))"
      }
    })
  }

  return options
}

export function getDisplayAndFullTimeZone(location) {
  let timeZoneDisplay = ""
  let timeZone = ""
  if (location?.kind === "virtual") {
    timeZoneDisplay = new Date().toLocaleTimeString("en-us", { timeZoneName: "short" }).split(" ")[2]
    timeZone = new Date().toLocaleTimeString("en-us", { timeZoneName: "long" }).split(" ").slice(2).join(" ")
  } else {
    timeZoneDisplay = location?.timeZoneAbbr
    timeZone = location?.formattedTimeZone
  }

  return [timeZoneDisplay, timeZone]
}

export function getDisplayServiceRanges(service) {
  let amountCentsRange = `${service.amountCents > 0 ? `$${service.amountCents / 100}` : "Free"}`
  let timeLengthRange = formatDuration(service.timeLength, service)
  if (service.variations.length > 1) {
    const lastService = service.variations[service.variations.length - 1]
    if (lastService.amountCents !== service.amountCents) {
      amountCentsRange += ` - ${lastService.amountCents > 0 ? `$${lastService.amountCents / 100}` : "Free"}`
    }
    if (lastService.timeLength !== service.timeLength) {
      timeLengthRange += ` - ${formatDuration(lastService.timeLength, service)}`
    }
  }
  return [amountCentsRange, timeLengthRange]
}

export function getServicePriceRange(service, currency = "USD") {
  const allAmounts = [service.amountCents, ...(service.variations || []).map((v) => v.amountCents)].filter(
    (a) => typeof a === "number"
  )

  if (allAmounts.length === 0) return ""

  const minAmount = Math.min(...allAmounts)
  const maxAmount = Math.max(...allAmounts)

  if (minAmount === maxAmount) {
    return minAmount === 0 ? "Free" : formatPrice(minAmount, currency)
  }

  const minText = minAmount === 0 ? "Free" : formatPrice(minAmount, currency)
  const maxText = maxAmount === 0 ? "Free" : formatPrice(maxAmount, currency)

  return `${minText} - ${maxText}`
}

export function getServiceDurationRange(service) {
  const allDurations = [service.timeLength, ...(service.variations || []).map((v) => v.timeLength)].filter(
    (d) => typeof d === "number"
  )

  if (allDurations.length === 0) return ""

  const minDuration = Math.min(...allDurations)
  const maxDuration = Math.max(...allDurations)

  if (minDuration === maxDuration) {
    return formatDuration(minDuration, service)
  }

  // For packages, we might want to stick to the main service format or simplify
  // Assuming standard duration formatting for ranges
  return `${minDuration} - ${maxDuration} mins`
}

export function validEmail(email) {
  return email.match(
    /^(([^<>()[\].,;:@"]+(\.[^<>()[\].,;:@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  )
}

export const sanitize = (value) => (typeof window !== "undefined" ? DOMPurify.sanitize(value) : value) // not sure why, but DOMPurify.sanitize is undefined in SSR

// Helper function to get fresh CSRF token for each request
const getCSRFToken = () => {
  if (typeof window === "undefined") return ""
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""
}

// Capture X-Request-Id from GraphQL responses so Honeybadger can correlate frontend errors with backend logs
const graphqlFetch = (url, options = {}) => {
  const baseFetch = options.fetch || fetch
  return baseFetch(url, options).then((response) => {
    const requestId = response.headers?.get?.("X-Request-Id")
    if (requestId && typeof window !== "undefined") {
      window.__lastGraphqlRequestId = requestId
    }
    // Safari / CDN / proxies occasionally return 204 No Content on POST; urql's fetch exchange
    // expects JSON and surfaces CombinedError "[Network] No Content". Normalize to empty GraphQL payload.
    if (response.status === 204 || response.status === 205) {
      const headers = new Headers(response.headers)
      headers.set("Content-Type", "application/json")
      return new Response(JSON.stringify({ data: null }), {
        status: 200,
        statusText: "OK",
        headers
      })
    }
    return response
  })
}

// Dynamic fetch options that retrieves fresh CSRF token for each request
const getFetchOptions = () => ({
  headers: {
    "X-CSRF-Token": getCSRFToken(),
    "Content-Type": "application/json",
    ...(browserIanaTimeZone() ? { "X-Client-Time-Zone": browserIanaTimeZone() } : {})
  },
  fetch: graphqlFetch
})

// Debug CSRF token retrieval (only in development)
if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
  const csrfToken = getCSRFToken()
  console.log("CSRF token found:", csrfToken ? "yes" : "no", "value:", csrfToken) // eslint-disable-line no-console
}

export const urqlClient = createClient({
  url: "/graphql",
  fetchOptions: getFetchOptions
})

export const createUrqlClient = (options) =>
  createClient({
    url: "/graphql",
    fetchOptions: getFetchOptions,
    ...options
  })

export const networkOnlyUrqlClient = createUrqlClient({ requestPolicy: "network-only" })

export const formatAvailableTimes = (availableTimes) => {
  availableTimes = JSON.parse(availableTimes)
  availableTimes = availableTimes.reduce((acc, day) => {
    const dayName = day[0]
    const startAndEndTimes = day[1]
    acc[dayName] = mapKeys(startAndEndTimes, (value, key) => {
      if (key === "blocks" && Array.isArray(value)) {
        // Handle blocks array specially
        return "blocks"
      }
      return camelCase(key)
    })

    // Process blocks if they exist
    if (acc[dayName].blocks && Array.isArray(acc[dayName].blocks)) {
      acc[dayName].blocks = acc[dayName].blocks.map((block) => mapKeys(block, (value, key) => camelCase(key)))
    }

    return acc
  }, {})
  return availableTimes
}

export const ORDERED_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]

export const copyToClipboard = async (
  text,
  showToast,
  successMessage = "Link copied!",
  errorMessage = "Failed to copy"
) => {
  try {
    await navigator.clipboard.writeText(text)
    showToast && showToast(successMessage)
  } catch (err) {
    showToast && showToast({ type: "error", content: errorMessage })
  }
}

export const commIntervals = [
  { value: 5, label: "5 mins" },
  { value: 15, label: "15 mins" },
  { value: 30, label: "30 mins" },
  { value: 60, label: "1 hrs" },
  { value: 120, label: "2 hrs" },
  { value: 240, label: "4 hrs" },
  { value: 480, label: "8 hrs" },
  { value: 1440, label: "24 hrs" },
  { value: 2880, label: "2 days" }
]

export const joinWithAnd = (array) => {
  if (array.length === 0) {
    return null
  } else if (array.length === 1) {
    return array[0]
  } else if (array.length === 2) {
    return `${array[0]} and ${array[1]}`
  } else {
    return `${array.slice(0, -1).join(", ")}, and ${array[array.length - 1]}`
  }
}

function hexToRgb(hex) {
  const bigint = parseInt(hex.slice(1), 16)
  return {
    r: (bigint >> 16) & 255,
    g: (bigint >> 8) & 255,
    b: bigint & 255
  }
}

function rgbToHex({ r, g, b }) {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1).toUpperCase()
}

export function blendColors(topColor, topOpacity, bottomColor) {
  const topRgb = hexToRgb(topColor)
  const bottomRgb = hexToRgb(bottomColor)

  const blendedRgb = {
    r: Math.round(topRgb.r * topOpacity + bottomRgb.r * (1 - topOpacity)),
    g: Math.round(topRgb.g * topOpacity + bottomRgb.g * (1 - topOpacity)),
    b: Math.round(topRgb.b * topOpacity + bottomRgb.b * (1 - topOpacity))
  }

  return rgbToHex(blendedRgb)
}

export function lightenHexColor(hex, percent) {
  if (!hex) return hex

  hex = hex.replace(/^#/, "")

  let r = parseInt(hex.substring(0, 2), 16)
  let g = parseInt(hex.substring(2, 4), 16)
  let b = parseInt(hex.substring(4, 6), 16)

  r = Math.min(255, Math.floor(r * (1 + percent / 100)))
  g = Math.min(255, Math.floor(g * (1 + percent / 100)))
  b = Math.min(255, Math.floor(b * (1 + percent / 100)))

  r = r.toString(16).padStart(2, "0")
  g = g.toString(16).padStart(2, "0")
  b = b.toString(16).padStart(2, "0")

  return `#${r}${g}${b}`
}

export const isValidUrl = (string) => {
  try {
    new URL(string)
    return true
  } catch (_) {
    return false
  }
}

export const stripHTMLTags = (htmlString) => {
  if (typeof window === "undefined") {
    // Server-side fallback using regex
    return htmlString
      .replace(/<[^>]*>/g, " ") // Replace HTML tags with spaces
      .replace(/\s+/g, " ") // Normalize whitespace
      .trim() // Remove leading/trailing whitespace
  }

  // Browser-side implementation with DOMParser
  const parser = new DOMParser()
  const doc = parser.parseFromString(htmlString, "text/html")
  const textWithSpaces = []

  function traverse(node) {
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        textWithSpaces.push(child.textContent.trim())
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        traverse(child)
        textWithSpaces.push(" ") // Add a space after each element node
      }
    })
  }

  traverse(doc.body)

  return textWithSpaces.join(" ").replace(/\s+/g, " ").trim() // Clean up extra spaces
}

/**
 * Smart duration formatting that shows hours instead of minutes for better perception
 * @param {number} timeLength - Duration in minutes
 * @param {object} service - Service object (optional, for package detection)
 * @returns {string} Formatted duration string
 */
export const formatDuration = (timeLength, service = null) => {
  if (!timeLength) return "Duration varies"

  // For packages with sessions, show sessions instead of time
  if (service?.package && service?.numberOfSessions) {
    return `${service.numberOfSessions} session${service.numberOfSessions > 1 ? "s" : ""}`
  }

  return `${timeLength} mins`
}

/**
 * Format duration with sessions info for packages
 * @param {number} timeLength - Duration in minutes
 * @param {object} service - Service object
 * @returns {string} Formatted duration string with sessions if applicable
 */
export const formatDurationWithSessions = (timeLength, service = null) => {
  if (!timeLength) return "Duration varies"

  // For packages with sessions, show sessions + duration of each session
  if (service?.package && service?.numberOfSessions) {
    const sessionCount = service.numberOfSessions

    // Format the duration of each session
    const sessionDuration = `${timeLength} mins each`

    return `${sessionCount} session${sessionCount > 1 ? "s" : ""} • ${sessionDuration}`
  }

  return `${timeLength} mins`
}
